import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../api/biApi";
import type { SmartViewArtifactFilter } from "../../../../../api/types/smartViewArtifactsTypes";
import type { DimensionDictionary } from "../../../../../api/types/svTypes";
import SelectedOptionsPanel from "../../../../common/filters/editors/SelectedOptionsPanel";
import type { SelectOption } from "../../../../common/filters/filterTypes";

interface Props {
  filter: SmartViewArtifactFilter;
  values: string[];
  labels: Record<string, string>;
  onChange: (values: string[], pickedLabels: Record<string, string>) => void;
  onClear: () => void;
  onClose: () => void;
}

const PAGE_SIZE = 200;
const MAX_SEARCH_LENGTH = 250;

const toOption = (row: DimensionDictionary, keyFieldName: string): SelectOption[] => {
  const key = row[keyFieldName];
  if (key === null || key === undefined) {
    return [];
  }
  const value = String(key);
  const name = row["Name"];
  return [{ value, label: name === null || name === undefined || name === "" ? value : String(name) }];
};

interface OptionRowProps {
  option: SelectOption;
  selected: boolean;
  singleSelect: boolean;
  onClick: (option: SelectOption) => void;
}

const OptionRow = ({ option, selected, singleSelect, onClick }: OptionRowProps) => (
  <ListItem dense disablePadding>
    <ListItemButton dense selected={selected} onClick={() => onClick(option)}>
      {!singleSelect && (
        <ListItemIcon sx={{ minWidth: 0 }}>
          <Checkbox edge="start" tabIndex={-1} disableRipple checked={selected} />
        </ListItemIcon>
      )}
      <ListItemText primary={option.label} slotProps={{ primary: { noWrap: true } }} />
    </ListItemButton>
  </ListItem>
);

const ValueFilterEditor = ({ filter, values, labels, onChange, onClear, onClose }: Props) => {
  const [searchTerm, setSearchTerm] = useState("");

  const searchValues = useCallback(
    (signal?: AbortSignal) =>
      biApi.searchDimensionDictionary(
        filter.dataSetId,
        {
          dimensionName: filter.dimensionName,
          range: { skip: 0, take: PAGE_SIZE, totals: false },
          searchTermUserInput: searchTerm || undefined,
        },
        signal
      ),
    [filter.dataSetId, filter.dimensionName, searchTerm]
  );
  const [result, searchError, { isFetching }] = useFetch(searchValues);

  const options = result ? result.items.flatMap((row) => toOption(row, result.keyFieldName)) : [];
  const selectedOptions = values.map((value) => ({
    value,
    label: labels[value] ?? options.find((option) => option.value === value)?.label ?? value,
  }));

  const handleOptionClick = (option: SelectOption) => {
    const pickedLabels = { [option.value]: option.label };
    if (filter.singleSelect) {
      onChange([option.value], pickedLabels);
      onClose();
      return;
    }
    const nextValues = values.includes(option.value)
      ? values.filter((value) => value !== option.value)
      : [...values, option.value];
    onChange(nextValues, pickedLabels);
  };

  const handleRemoveValue = (removedValue: string) => {
    onChange(
      values.filter((value) => value !== removedValue),
      {}
    );
  };

  if (searchError) {
    logError(searchError, "[ValueFilterEditor] searchDimensionDictionary");
  }

  return (
    <Stack spacing={1} sx={(t) => ({ width: t.spacing(47.5), overflow: "hidden" })}>
      <Stack spacing={2} sx={{ px: 2, pt: 2, pb: 1 }}>
        {values.length > 0 && (
          <SelectedOptionsPanel
            selectedValues={values}
            options={selectedOptions}
            onRemoveOption={handleRemoveValue}
            onClear={onClear}
          />
        )}
        <SearchField fullWidth autoFocus debounceTimeMs={300} maxLength={MAX_SEARCH_LENGTH} onSearch={setSearchTerm} />
      </Stack>
      <Divider />
      {isFetching && (
        <Box sx={(t) => ({ height: t.spacing(25) })}>
          <InlineLoader />
        </Box>
      )}
      {!isFetching && searchError && (
        <Typography color="error" sx={{ px: 2, pb: 2 }}>
          Failed to load the values.
        </Typography>
      )}
      {!isFetching && !searchError && options.length === 0 && (
        <Typography color="textSecondary" sx={{ px: 2, pb: 2 }}>
          No values found.
        </Typography>
      )}
      {!isFetching && !searchError && options.length > 0 && (
        <List sx={(t) => ({ overflowY: "auto", px: 2, pt: 0, maxHeight: t.spacing(55) })}>
          {options.map((option) => (
            <OptionRow
              key={option.value}
              option={option}
              selected={values.includes(option.value)}
              singleSelect={filter.singleSelect}
              onClick={handleOptionClick}
            />
          ))}
        </List>
      )}
      {!isFetching && !searchError && result?.items.length === PAGE_SIZE && (
        <Typography variant="caption" color="textSecondary" sx={{ px: 2, pb: 1 }}>
          Showing the first {PAGE_SIZE} values. Search to find others.
        </Typography>
      )}
    </Stack>
  );
};

export default ValueFilterEditor;
